import { useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Activity,
  BookOpen,
  Building2,
  CalendarCheck,
  FileText,
  MousePointerClick,
  Globe,
  KeySquare,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  Menu,
  Receipt,
  Settings,
  ShieldAlert,
  ShieldCheck,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { api, useLogoutMutation, useMeQuery, usePartnerQuery } from '@/store/api'
import { useDispatch } from 'react-redux'
import { Select } from '@/components/ui/select'
import { STATUS_PAGE_URL } from '@/lib/links'
import { fmtDate } from '@/lib/format'

const ROLE_LABELS = { atarim_admin: 'Atarim admin', partner_user: 'Partner user', group_user: 'Group user' } as const
const SUSPENSION_LABELS = { non_payment: 'non-payment (clause 6.7)', material_breach: 'material breach (clause 17.2)' } as const

/**
 * 240px sidebar + scrollable content, max-w 1280 (design.md §4). Below `lg` the sidebar
 * becomes a drawer opened from a top bar, so the brand switcher stays usable.
 */
export function AppShell() {
  const { data: me } = useMeQuery()
  const params = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [logout] = useLogoutMutation()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const activeSlug = params.slug ?? location.pathname.match(/^\/p\/([^/]+)/)?.[1] ?? null
  const { data: partner } = usePartnerQuery(activeSlug ?? '', { skip: !activeSlug })

  if (!me) return null
  const isAdmin = me.role === 'atarim_admin'

  const switchPartner = (slug: string) => {
    // Keep the current section when switching brand (design.md §4)
    const section = location.pathname.match(/^\/p\/[^/]+\/(.+)$/)?.[1] ?? 'overview'
    navigate(`/p/${slug}/${section}`)
  }

  const sidebar = (
    <>
      <div className="flex items-center gap-2 px-5 pb-2 pt-5">
        <div className="flex size-7 items-center justify-center rounded-lg bg-primary font-bold text-primary-foreground">A</div>
        <div className="text-sm font-semibold">
          Atarim <span className="text-muted-foreground">Partners</span>
        </div>
      </div>

      {activeSlug && me.partners.length > 1 && (
        <div className="px-4 pt-3">
          <Select
            ariaLabel="Switch brand"
            value={me.partners.find((p) => p.slug === activeSlug)?.slug ?? activeSlug}
            onValueChange={switchPartner}
            options={me.partners.map((p) => ({ value: p.slug, label: p.name }))}
            className="w-full"
          />
        </div>
      )}
      {activeSlug && me.partners.length === 1 && <div className="px-5 pt-3 text-sm font-medium">{me.partners[0]?.name}</div>}

      <nav className="mt-4 flex flex-1 flex-col gap-0.5 px-3" onClick={() => setDrawerOpen(false)}>
        {isAdmin && (
          <>
            <SideLink to="/admin/partners" icon={<LayoutGrid className="size-4" />} label="Partners" />
            <SideLink to="/admin/close" icon={<CalendarCheck className="size-4" />} label="Month-end close" />
            <SideLink to="/admin/interest" icon={<MousePointerClick className="size-4" />} label="Partner interest" />
            <SideLink to="/admin/admins" icon={<ShieldCheck className="size-4" />} label="Atarim admins" />
          </>
        )}
        {me.group && (
          <SideLink to={`/g/${me.group.slug}`} icon={<Building2 className="size-4" />} label="Group summary" />
        )}
        {activeSlug && (
          <>
            {isAdmin && <div className="mb-1 mt-4 px-2 text-xs uppercase tracking-wide text-muted-foreground">Partner view</div>}
            <SideLink to={`/p/${activeSlug}/overview`} icon={<LayoutDashboard className="size-4" />} label="Overview" />
            {partner?.offers.agency !== false && (
              <SideLink to={`/p/${activeSlug}/licences`} icon={<KeySquare className="size-4" />} label="Agency licences" />
            )}
            {partner?.offers.smb && <SideLink to={`/p/${activeSlug}/sites`} icon={<Globe className="size-4" />} label="SMB sites" />}
            <SideLink to={`/p/${activeSlug}/charges`} icon={<Receipt className="size-4" />} label="Charges" />
            <SideLink to={`/p/${activeSlug}/statements`} icon={<FileText className="size-4" />} label="Statements" />
            <SideLink to={`/p/${activeSlug}/settings`} icon={<Settings className="size-4" />} label="Settings" />
          </>
        )}
        <div className="mt-auto flex flex-col gap-0.5 pb-2 pt-4">
          <SideLink to="/glossary" icon={<BookOpen className="size-4" />} label="Glossary" />
          <a
            href={STATUS_PAGE_URL}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground"
          >
            <Activity className="size-4" />
            Service status
          </a>
        </div>
      </nav>

      <div className="border-t border-border p-4">
        <div className="truncate text-sm font-medium">{me.email}</div>
        <div className="text-xs text-muted-foreground">
          {ROLE_LABELS[me.role]}
          {me.group && ` · ${me.group.name}`}
        </div>
        <button
          onClick={async () => {
            await logout()
            dispatch(api.util.resetApiState())
            navigate('/login')
          }}
          className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <LogOut className="size-3.5" /> Log out
        </button>
      </div>
    </>
  )

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-border bg-card px-4 py-3 lg:hidden">
        <button aria-label="Open menu" onClick={() => setDrawerOpen(true)} className="rounded-lg p-1.5 hover:bg-muted">
          <Menu className="size-5" />
        </button>
        <span className="truncate text-sm font-semibold">{partner?.name ?? 'Atarim Partners'}</span>
      </header>

      {drawerOpen && <div className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={() => setDrawerOpen(false)} />}
      <aside
        className={cn(
          'fixed inset-y-0 z-50 flex w-60 flex-col border-r border-border bg-card transition-transform lg:translate-x-0',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <button
          aria-label="Close menu"
          onClick={() => setDrawerOpen(false)}
          className="absolute right-3 top-4 rounded-lg p-1 hover:bg-muted lg:hidden"
        >
          <X className="size-4" />
        </button>
        {sidebar}
      </aside>

      <main className="lg:ml-60">
        <div className="mx-auto max-w-[1280px] px-4 py-6 lg:px-8">
          {partner?.suspension && (
            <div className="mb-6 flex items-start gap-3 rounded-xl border border-ata-red/40 bg-ata-red/10 px-4 py-3 text-sm">
              <ShieldAlert className="mt-0.5 size-4 shrink-0 text-ata-red" />
              <div>
                <span className="font-medium">{partner.name} is suspended</span> since {fmtDate(partner.suspension.suspendedAt)} for{' '}
                {SUSPENSION_LABELS[partner.suspension.reason]}. New agency licences and SMB enrolments are blocked. Existing customers
                keep running.{' '}
                <Link to="/glossary#suspension" className="underline">
                  What this means
                </Link>
              </div>
            </div>
          )}
          <Outlet />
        </div>
      </main>
    </div>
  )
}

function SideLink({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium',
          isActive ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        )
      }
    >
      {icon}
      {label}
    </NavLink>
  )
}

/** Page header pattern: h1 + optional description + right-aligned actions (design.md §4). */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string
  description?: React.ReactNode
  actions?: React.ReactNode
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold">{title}</h1>
        {description && <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}
